import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { ConnectOptions } from 'twilio-video';

import notificationCheckin from 'assets/audio/checkin/check-in.mp3';

import {
  useHandleRoomDisconnection,
  useHandleTrackPublicationFailed,
  useLocalTracks,
  useRestartAudioTrackOnDeviceChange,
  useRoom,
  useScreenShareToggle,
  useSignalrConnection,
} from 'hooks';
import { ApiUser, ErrorCallback, HubPatient } from 'types';
import { ISignalrConnectionContext } from 'context';
import { VideoContext } from './context';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';

type VideoProviderProps = {
  options?: ConnectOptions;
  children?: JSX.Element;
};

export function VideoProvider({ children, options }: VideoProviderProps) {
  const { t } = useTranslation('providerRoom');
  const onErrorCallback: ErrorCallback = useCallback((error) => {
    // eslint-disable-next-line no-console
    console.log(`ERROR: ${error.message}`, error);
  }, []);

  const {
    localTracks,
    getLocalVideoTrack,
    getLocalAudioTrack,
    isAcquiringLocalTracks,
    removeLocalAudioTrack,
    removeLocalVideoTrack,
    getAudioAndVideoTracks,
  } = useLocalTracks();
  const { enqueueSnackbar } = useSnackbar();
  const { room, isConnecting, connect } = useRoom(localTracks, onErrorCallback, options);
  const { events, signalrConnectionId, isRequeuing }: ISignalrConnectionContext = useSignalrConnection();
  const user: ApiUser | null = useAppSelector(selectUser);

  const [isSharingScreen, toggleScreenShare] = useScreenShareToggle(room, onErrorCallback);
  const [userJoined, setUserJoined] = useState<HubPatient | null>(null);

  const prevSignalrConnectionId: MutableRefObject<string> = useRef<string>(signalrConnectionId);
  const isTransferringRef = useRef<boolean>(false);
  const setIsTransferring = useCallback((value: boolean) => {
    isTransferringRef.current = value;
  }, []);

  useEffect(() => {
    if (userJoined) {
      // eslint-disable-next-line no-console
      console.log('onUserJoined');

      // Check if notification sound is enabled before playing it
      if (user?.clinicUser?.notificationsSound) {
        const audio = new Audio(notificationCheckin);
        audio.play().catch((error) => console.error('Error playing checkin sound:', error));
      }

      if ((user?.clinicUser?.receiveMessagesInMeeting || !room) && !isRequeuing.current) {
        enqueueSnackbar(t('userJoinedQueue', { userName: userJoined.userName }), { variant: 'info' });
      }

      setUserJoined(null);
    }
  }, [userJoined, user, room, t]);

  useEffect(() => {
    events.on('userJoined', (data: HubPatient) => setUserJoined(data));
  }, [events]);

  useEffect(() => {
    return () => {
      if (!signalrConnectionId && prevSignalrConnectionId.current && !isTransferringRef.current) {
        removeLocalVideoTrack();
        removeLocalAudioTrack();
      }

      prevSignalrConnectionId.current = signalrConnectionId;
    };
  }, [signalrConnectionId, removeLocalVideoTrack, removeLocalAudioTrack]);

  // Register callback functions to be called on room disconnect.
  useHandleRoomDisconnection(
    room,
    onErrorCallback,
    removeLocalAudioTrack,
    removeLocalVideoTrack,
    isSharingScreen,
    toggleScreenShare,
  );
  useHandleTrackPublicationFailed(room, onErrorCallback);
  useRestartAudioTrackOnDeviceChange(localTracks);
  useEffect(() => {
    const handleForceMediaRelease = () => {
      try {
        if (isSharingScreen) {
          toggleScreenShare();
        }
      } catch (error) {
        console.error('Failed to stop screen share during logout:', error);
      }

      try {
        if (room) {
          room.localParticipant.audioTracks.forEach(({ track }) => {
            room.localParticipant.unpublishTrack(track);
            track.stop();
          });

          room.localParticipant.videoTracks.forEach(({ track }) => {
            room.localParticipant.unpublishTrack(track);
            track.stop();
          });

          room.disconnect();
        }
      } catch (error) {
        console.error('Failed to disconnect room during logout:', error);
      }

      removeLocalAudioTrack();
      removeLocalVideoTrack();

      // Defensive cleanup for any media elements still holding active streams.
      document.querySelectorAll('video, audio').forEach((element) => {
        const mediaElement = element as HTMLMediaElement;
        const stream = mediaElement.srcObject as MediaStream | null;
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
          mediaElement.srcObject = null;
        }
      });
    };

    window.addEventListener('app-force-media-release', handleForceMediaRelease);
    return () => window.removeEventListener('app-force-media-release', handleForceMediaRelease);
  }, [room, isSharingScreen, toggleScreenShare, removeLocalAudioTrack, removeLocalVideoTrack]);

  return (
    <VideoContext.Provider
      value={{
        room,
        localTracks,
        isConnecting,
        connect,
        getLocalVideoTrack,
        getLocalAudioTrack,
        isAcquiringLocalTracks,
        removeLocalVideoTrack,
        removeLocalAudioTrack,
        isSharingScreen,
        toggleScreenShare,
        getAudioAndVideoTracks,
        onError: onErrorCallback,
        setIsTransferring,
      }}
    >
      {children}
    </VideoContext.Provider>
  );
}
